import React, { Fragment, type ReactElement, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import * as Yup from "yup"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  SvgIcon,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material"
import {
  Button,
  InputText,
  Menu,
  MenuItem,
  NotificationUtils,
  Tooltip,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FieldArray, FormikProvider, useFormik } from "formik"
import { t } from "i18next"
import moment from "moment"
import { v4 as uuidv4 } from "uuid"

import { KonanAvatar } from "../../../components/core/Avatar"
import { BaseSimpleDialog } from "../../../components/dialogs/BaseSimpleDialog"
import { VersioningDialog } from "../../../components/dialogs/VersioningDialog"
import { VersionTag } from "../../../components/status/VersionTag"
import { KonanEmptyState } from "../../../components/utility/KonanEmptyState"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { CreateProgramRequest, UpdateProgramRequest, baseErrorType } from "../../../types/custom/projects"
import type { VersionChangeRequest } from "../../../types/custom/rules"
import type { PaginatedProgramVersionListList } from "../../../types/generated/api/PaginatedProgramVersionListList"
import type { Program } from "../../../types/generated/api/Program"
import { ProgramItem } from "../../../types/generated/api/ProgramItem"
import type { ProgramRetrieve } from "../../../types/generated/api/ProgramRetrieve"
import type { ProgramVersionList } from "../../../types/generated/api/ProgramVersionList"
import type { ProgramVersionRetrieve } from "../../../types/generated/api/ProgramVersionRetrieve"
import { getValidMaxMinorVersion } from "../../../utils/deploymentDetailsHelpers"
import { extractPageFromBackEndPaginationLink } from "../../../utils/genericHelpers"
import { COMPONENT_TYPES, type SelectedComponent } from "../../Predictions/Interfaces"
import { ComponentTestingDialog } from "../../Predictions/components/ComponentTestingDialog"
import { DataBlock } from "../../Ruleset/components/RuleCard"
import { LiveModelCard } from "./ProjectCard"
import { ScoreCardSetBlock } from "./ScoreCardSetBlock"

import styles from "../Programs.module.scss"

// type for URL params
type ParamsType = {
  id: string
}

interface ItemType {
  item_type: ProgramItem.item_type
  scorecard_set?: { name: string; uuid: string }
  project?: { name: string; uuid: string }
  weight: string
}

type Props = {
  isLoading?: boolean
  program?: Program
  handleCancel?: () => void
  isNewProgram?: boolean
  key?: string
  scorecardSetCount?: number
  isScorecardSetsLoading?: boolean
  projectsCount?: number
  isProjectsLoading?: boolean
  disableDuplicateButton?: boolean
  duplicateProgram?: (program: Program) => void
  canShowDetails?: boolean
  isDuplicate?: boolean
}

// Yup validation schema
const validationSchemaScorecard = Yup.object({
  name: Yup.string().required(t("ProgramCard.validation.programNameRequired", { ns: "programs" })),
  items: Yup.array().of(
    Yup.object().shape({
      item_type: Yup.string().required(t("ProgramCard.validation.itemTypeRequired", { ns: "programs" })),
      scorecard_set: Yup.object({
        name: Yup.string(),
        uuid: Yup.string(),
      }).when(["item_type"], {
        is: (item_type: string) => item_type === "scorecardset",
        then: () =>
          Yup.object({
            name: Yup.string().required(t("ProgramCard.validation.scorecardNameRequired", { ns: "programs" })),
            uuid: Yup.string().required(t("ProgramCard.validation.scorecardUuidRequired", { ns: "programs" })),
          }),
      }),
      project: Yup.object({
        name: Yup.string(),
        uuid: Yup.string(),
      }).when(["item_type"], {
        is: (item_type: string) => item_type === "project",
        then: () =>
          Yup.object({
            name: Yup.string().required(t("ProgramCard.validation.projectNameRequired", { ns: "programs" })),
            uuid: Yup.string().required(t("ProgramCard.validation.projectUuidRequired", { ns: "programs" })),
          }),
      }),
      weight: Yup.number().required(t("ProgramCard.validation.weightRequired", { ns: "programs" })),
    }),
  ),
})

function ProgramLoadingComponent(): ReactElement {
  return (
    <Grid item xs={12}>
      <Card className="card-box-shadow">
        <CardHeader className={styles.scorecardSetHeader} title={<Skeleton width={200} />} />
        <CardContent className={styles.cardContent}>
          <Skeleton height={30} />
          <Skeleton height={30} />
        </CardContent>
      </Card>
    </Grid>
  )
}

/**
 * Single Program component
 * @param {boolean} isLoading indicator for Program data loading or not
 * @param {function} handleCancel handler for opting out of Program creation
 * @param {Program} program program item
 * @param {Program} allPrograms program item
 * @param {boolean} isNewProgram enable/disable new program mode
 * @param {string} key unique key for the program
 * @param {Array<Deployment>} projectsCount count of existing of projects
 * @param {PaginatedScoreCardSetListList} scorecardSetCount count of existing scorecard sets
 * @param {boolean} isScorecardSetsLoading indicator for scorecard sets loading or not
 * @param {boolean} isProjectsLoading indicator for projects loading or not
 * @param {boolean} disableDuplicateButton indicator for duplicate button
 * @param {function} duplicateProgram handler for duplicating a program
 * @returns {React.ReactElement}
 */
export function ProgramCard(props: Readonly<Props>): React.ReactElement {
  const {
    program,
    handleCancel,
    isLoading,
    isNewProgram = false,
    key,
    scorecardSetCount,
    isScorecardSetsLoading,
    projectsCount,
    isProjectsLoading,
    disableDuplicateButton,
    duplicateProgram,
    canShowDetails,
    isDuplicate,
  } = props

  const { id: projectId } = useParams<ParamsType>()

  const queryClient = useQueryClient()

  const MuiTheme = useMuiTheme()
  const theme = getTheme()
  const fullScreen = useMediaQuery(MuiTheme.breakpoints.down("md"))

  const { t } = useTranslation("programs")

  const { ref: lastVersionRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isProgramVersionsListLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null)
  const [isEditMode, setIsEditMode] = useState<boolean>(isNewProgram ?? false)
  const [isVersioningDialogOpen, setIsVersioningDialogOpen] = useState<boolean>(false)
  const [isTestDialogOpen, setIsTestDialogOpen] = useState<boolean>(false)
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined)

  // Stores the latest minor version for each major version to determine valid restoration points
  // TODO: Move this state into a separate ProgramVersioningDialog component along with version related logic
  // for better separation of concerns
  const [maxMinorVersionsMap, setMaxMinorVersionsMap] = useState<Record<string, string>>({})

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState<boolean>(false)

  const [anchor2, setAnchor2] = React.useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchor2)

  const userEmail = useSelector((state: RootState) => state.auth.email)

  // fetch single program
  const { data: programData, isLoading: isProgramDataLoading } = useQuery<AxiosResponse<ProgramRetrieve>, AxiosError>(
    ["program", projectId, program?.uuid],
    () => KonanAPI.fetchProgram(projectId as string, program?.uuid as string),
    {
      onSuccess: ({ data }) => {
        // to overwrite items data in formik
        formik.setFieldValue("items", data?.active_version?.items)
      },
      enabled: (!!projectId && program?.uuid !== "new" && canShowDetails) || isDuplicate,
    },
  )

  const {
    isLoading: isProgramVersionsListLoading,
    isFetchingNextPage,
    data: programVersionsList,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedProgramVersionListList>, AxiosError, ProgramVersionList>(
    ["programVersionsList", projectId, program?.uuid],
    ({ pageParam = 1 }) =>
      KonanAPI.listProgramVersions({
        projectId: projectId as string,
        programId: program?.uuid as string,
        page_size: 10,
        page: pageParam,
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<ProgramVersionList[]>(
          (accumulator, page) => accumulator.concat(page.data.results as ProgramVersionList[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId && !!program?.uuid && isVersioningDialogOpen,
      refetchOnMount: true,
    },
  )

  // fetch Version
  const { data: programVersionData, isLoading: isProgramVersionDataLoading } = useQuery<
    AxiosResponse<ProgramVersionRetrieve>,
    AxiosError
  >(
    ["selectedProgramVersion", projectId, program?.uuid, selectedVersion],
    () =>
      KonanAPI.retrieveProgramVersion({
        projectId: projectId as string,
        programId: program?.uuid as string,
        version: selectedVersion as string,
      }),
    {
      onSuccess: async () => {
        if (
          selectedVersion &&
          !maxMinorVersionsMap[selectedVersion?.split(".")?.[0] as string] &&
          programVersionsList?.pages
        ) {
          const result = getValidMaxMinorVersion(programVersionsList?.pages, selectedVersion)
          setMaxMinorVersionsMap((prevResults) => ({
            ...prevResults,
            ...result,
          }))
        }
      },
      enabled: !!projectId && !!program?.uuid && isVersioningDialogOpen && !!selectedVersion,
    },
  )

  const createProgram = useMutation<AxiosResponse<Program>, AxiosError<baseErrorType>, CreateProgramRequest>(
    KonanAPI.createProgram,
    {
      onError: (data) => {
        // TODO:: check error types with backend
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          NotificationUtils.toast(data.response?.data.detail[0] ?? t("notifications.programMutationError"), {
            severity: "error",
          })
        } catch (e) {
          NotificationUtils.toast(t("notifications.programMutationError"), {
            severity: "error",
          })
        }
      },
      onSuccess: async () => {
        isNewProgram && handleCancel?.()

        await queryClient.invalidateQueries("programs")
        await queryClient.invalidateQueries("program")
      },
    },
  )

  const updateProgram = useMutation<AxiosResponse, AxiosError<baseErrorType>, UpdateProgramRequest>(
    KonanAPI.updateProgram,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries("programs")
        await queryClient.invalidateQueries("program")
      },
      onError: (data) => {
        // TODO:: check error types with backend
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          NotificationUtils.toast(data.response?.data.detail[0] ?? t("notifications.programMutationError"), {
            severity: "error",
          })
        } catch (e) {
          NotificationUtils.toast(t("notifications.programMutationError"), {
            severity: "error",
          })
        }
      },
    },
  )

  // ruleset deletion Mutation
  const deleteProgramMutation = useMutation<
    AxiosResponse,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    { project_uuid: string; resource_uuid: string }
  >((props) => KonanAPI.deleteWorkflowResource({ ...props, resource_type: "programs" }), {
    onSuccess: async () => {
      NotificationUtils.toast(t("notifications.programDeleted"), { severity: "success" })

      await queryClient.invalidateQueries("programs")
      await queryClient.invalidateQueries("program")
    },
    onError: ({ response }) => {
      NotificationUtils.toast(response?.data?.details ?? t("notifications.programDeleteError"), { severity: "error" })
    },
  })

  const changeActiveProgramVersion = useMutation<AxiosResponse, AxiosError, VersionChangeRequest>(
    KonanAPI.changeResourceVersion,
  )

  const formik = useFormik({
    validationSchema: validationSchemaScorecard,
    initialValues: {
      name: program?.name ?? "",
      items: program?.items ?? [],
    },
    onSubmit: async (values) => {
      let TotalWeightSum = 0
      const trimmedProgramName = values.name.trim()

      for (const item of values.items) TotalWeightSum += parseFloat(item.weight.toString())

      if (TotalWeightSum < 1) {
        NotificationUtils.toast(t("notifications.wieghtsError"), {
          severity: "error",
        })
        return
      }

      if (isNewProgram) {
        await createProgram.mutateAsync({
          name: trimmedProgramName,
          items: values.items.map((item) => {
            return {
              ...(item.item_type === ProgramItem.item_type.PROJECT
                ? { project: item.project.uuid, weight: item.weight, item_type: item.item_type }
                : { scorecard_set: item.scorecard_set.uuid, weight: item.weight, item_type: item.item_type }),
            }
          }),
          projectId: projectId as string,
        })
      } else {
        await updateProgram.mutateAsync({
          name: trimmedProgramName !== program?.name ? trimmedProgramName : undefined,
          items: values.items.map((item) => {
            return {
              ...(item.item_type === ProgramItem.item_type.PROJECT
                ? { project: item.project.uuid, weight: item.weight, item_type: item.item_type }
                : { scorecard_set: item.scorecard_set.uuid, weight: item.weight, item_type: item.item_type }),
            }
          }),
          projectId: projectId as string,
          programId: program?.uuid as string,
        })
      }

      setIsEditMode(false)
    },
  })

  const changeProgramVersion = async (version: string): Promise<void> => {
    try {
      await changeActiveProgramVersion.mutateAsync({
        projectId: projectId as string,
        resourceId: program?.uuid as string,
        version,
        resource: "programs",
      })

      NotificationUtils.toast(t("notifications.programVersionChanged"), {
        severity: "success",
      })

      await queryClient.invalidateQueries(["programs", projectId])
      await queryClient.invalidateQueries(["program", projectId, program?.uuid])
      await queryClient.invalidateQueries(["programVersionsList", projectId, program?.uuid])
    } catch (error) {
      NotificationUtils.toast(t("notifications.programVersionChangeError"), {
        severity: "error",
      })
    }
  }

  // resetting values when cancelling editing
  const handleCancelEdit = (): void => {
    formik.resetForm()
    formik.setFieldValue("name", program?.name)
    formik.setFieldValue("items", program?.items)

    setIsEditMode(false)
  }

  // helper function for the versioning dialog
  // returns data of the version selected
  const GetProgramContainer = (): React.ReactElement => {
    const version = programVersionData?.data

    // extracting the current major version
    const majorSelectedVersion = (selectedVersion ?? programData?.data.active_version?.version)?.split(".")?.[0]

    // show restore button when the current major version from the selected version/active version existed in our valid versions hashMap
    // and it's not the current active version
    const shouldShowRestoreButton =
      maxMinorVersionsMap[majorSelectedVersion as string] ===
        (selectedVersion ?? programData?.data.active_version?.version) &&
      maxMinorVersionsMap[majorSelectedVersion as string] !== programData?.data.active_version?.version

    if (isProgramVersionsListLoading || isProgramVersionDataLoading) {
      return (
        <Grid container xs={12} gap={1} direction={"column"}>
          <Grid item className={"versioning-dialog-header"}>
            <Skeleton height={30} width={300} />
          </Grid>

          <Grid item px={2}>
            <ProgramLoadingComponent />
          </Grid>
        </Grid>
      )
    }

    return (
      <Grid container>
        {/* Header */}
        <Grid
          item
          xs={12}
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          className={"versioning-dialog-header"}
        >
          <Grid item gap={1} xs={9} display="flex">
            <Typography variant="h2-bold" style={{ textTransform: "inherit", marginRight: "5px" }}>
              {program?.name}
            </Typography>

            <VersionTag
              version={version?.version ?? selectedVersion}
              color={
                version ? (programData?.data.active_version?.version === selectedVersion ? "green" : "blue") : "green"
              }
            />
          </Grid>

          {shouldShowRestoreButton && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => changeProgramVersion(selectedVersion as string)}
              disabled={changeActiveProgramVersion.isLoading}
              isLoading={changeActiveProgramVersion.isLoading}
            >
              {t("restoreBtn")}
            </Button>
          )}
        </Grid>

        {/* Body */}
        <Grid item xs={12} p={1.5} pt={0}>
          {isProgramVersionDataLoading || version === undefined ? (
            <Grid container mt={2}>
              <Grid item xs={12} textAlign={"center"}>
                <CircularProgress size={24} style={{ color: theme.palette.blue.text[2] }} />
              </Grid>
              <Grid item xs={12} style={{ textAlignLast: "center" }}>
                <Typography variant="h3-regular">{t("fetchingVersion")}</Typography>
              </Grid>
            </Grid>
          ) : version?.items?.length > 0 ? (
            version?.items?.map((item, index) => (
              <Grid container item key={index} justifyContent="flex-start" alignItems="center" spacing={2} pt={1.5}>
                <Grid item xs={12}>
                  <Grid container item xs={12} spacing={2}>
                    <Grid item xs={10}>
                      {item.item_type === "project" ? (
                        <LiveModelCard project={item.project} isEdit={false} />
                      ) : (
                        <ScoreCardSetBlock scorecardset={item.scorecard_set} isEdit={false} />
                      )}
                    </Grid>
                    <Grid item xs={2} alignSelf={"center"}>
                      {isEditMode ? (
                        <InputText
                          id={`items[${index}].weight`}
                          placeholder={t("inputWeightPlaceholder")}
                          value={`${item.weight}`}
                          handleChange={formik.handleChange}
                          handleBlur={formik.handleBlur}
                          hideDescription
                          disabled={formik.isSubmitting}
                          fullWidth
                          key={key}
                          type="number"
                        />
                      ) : (
                        <DataBlock value={`${parseFloat(item.weight.toString())}%`} />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            ))
          ) : (
            <Grid
              container
              height={"100%"}
              flexDirection="column"
              justifyContent="flex-start"
              alignItems="center"
              pt={1.5}
            >
              <KonanEmptyState
                title={t("emptyState.noProgramsInVersionTitle")}
                subTitle={t("emptyState.noProgramsInVersionSubtitle")}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    )
  }

  const nonSelectedScoreCardSetsExist =
    (scorecardSetCount ?? 0) -
      formik.values.items.filter((item) => item.item_type === ProgramItem.item_type.SCORECARDSET).length ===
    0

  const nonSelectedProjectsExist =
    (projectsCount ?? 0) -
      formik.values.items.filter((item) => item.item_type === ProgramItem.item_type.PROJECT).length ===
    0

  const selectedProgram: SelectedComponent = useMemo(
    () => ({
      id: program?.uuid ?? "",
      type: COMPONENT_TYPES.PROGRAM,
      data: program as Program,
    }),
    [program],
  )

  return (
    <Fragment>
      {/* Testing Dialog */}
      {isTestDialogOpen && (
        <ComponentTestingDialog
          isOpen={isTestDialogOpen}
          handleClose={() => setIsTestDialogOpen(false)}
          selectedComponent={selectedProgram}
        />
      )}

      {isVersioningDialogOpen && (
        <VersioningDialog
          activeVersion={programData?.data.active_version as Program}
          setSelectedVersion={setSelectedVersion}
          selectedVersion={selectedVersion}
          isOpen={isVersioningDialogOpen}
          versions={programVersionsList?.pages ?? []}
          onClose={() => setIsVersioningDialogOpen(false)}
          height={fullScreen ? "100%" : "400px"}
          infiniteScrollProps={{
            hasNextPage,
            isFetchingNextPage,
            lastVersionRef,
          }}
          maxWidth="md"
        >
          <GetProgramContainer />
        </VersioningDialog>
      )}

      {isDeletionDialogOpen && (
        <BaseSimpleDialog
          open={isDeletionDialogOpen}
          name={program?.name ?? ""}
          onClose={() => setIsDeletionDialogOpen(false)}
          onAccept={() => {
            deleteProgramMutation.mutateAsync({
              project_uuid: projectId as string,
              resource_uuid: program?.uuid as string,
            })
          }}
          mode={"program-deletion"}
          isLoading={deleteProgramMutation.isLoading}
        />
      )}

      {canShowDetails && (isLoading || isProgramDataLoading) ? (
        <ProgramLoadingComponent />
      ) : (
        <Card className="card-box-shadow">
          <CardHeader
            className={styles.scorecardSetHeader}
            disableTypography
            title={
              <Grid container direction="column" item xs={12} spacing={1}>
                {/* Program Name */}
                <Grid item container xs={12} justifyContent="space-between">
                  <Grid item xs={6} md={3}>
                    {!isEditMode ? (
                      <Grid display={"flex"} alignItems={"center"}>
                        <Typography noWrap variant="h3-bold" style={{ textTransform: "inherit" }}>
                          {program?.name}
                        </Typography>
                      </Grid>
                    ) : (
                      <InputText
                        id="name"
                        hideDescription
                        placeholder={t("inputNamePlaceholder")}
                        value={formik.values.name}
                        handleChange={formik.handleChange}
                        error={formik.touched.name && Boolean(formik.errors.name) && formik.errors.name}
                        handleBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        fullWidth
                      />
                    )}
                  </Grid>

                  {!isEditMode && (
                    <Grid container item xs justifyContent={"flex-end"}>
                      <IconButton
                        aria-label="settings"
                        size="small"
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchor2(event.currentTarget)}
                      >
                        <MoreHorizIcon htmlColor={theme.palette.base.text[1]} />
                      </IconButton>

                      <Menu
                        key="basic-menu"
                        anchorEl={anchor2}
                        open={isMenuOpen}
                        onClose={() => setAnchor2(null)}
                        menuMaxContent
                      >
                        <MenuItem
                          onClick={() => {
                            setIsTestDialogOpen(true)
                            setAnchor2(null)
                          }}
                          className={styles.optionsMenuItem}
                        >
                          <Typography variant="a" color="base" variantColor={2}>
                            {t("testBtn")}
                          </Typography>
                        </MenuItem>
                        {canShowDetails && (
                          <MenuItem
                            onClick={() => {
                              setIsDeletionDialogOpen(true)
                              setAnchor2(null)
                            }}
                          >
                            <Typography variant="a" color="negative" variantColor={2}>
                              {t("removeProgramBtn")}
                            </Typography>
                          </MenuItem>
                        )}
                      </Menu>
                    </Grid>
                  )}
                </Grid>

                <Grid item container xs={12} justifyContent="space-between">
                  <Grid
                    sx={{ flexDirection: { xs: "column", md: "row" } }}
                    item
                    container
                    xs={12}
                    justifyContent="space-between"
                  >
                    <Grid container item spacing={1} xs={6} alignItems="flex-start" className={styles.avatarContainer}>
                      <Grid item sx={{ marginTop: "2px" }}>
                        {/* SvgIcon is used to fix square avatars on safari */}
                        <SvgIcon className={styles.modelAvatar}>
                          <KonanAvatar size={24} name={program?.created_by ?? (userEmail as string)} />
                        </SvgIcon>
                      </Grid>
                      <Grid container item xs={10}>
                        <Grid item>
                          <Typography variant="label-bold" noWrap style={{ width: "fit-content", marginTop: "0px" }}>
                            {program?.created_by ?? userEmail}
                          </Typography>

                          {program?.created_at && (
                            <Typography variant="label-regular" noWrap style={{ width: "fit-content" }}>
                              <Tooltip
                                title={moment(program?.created_at).format("DD/MM/YYYY, h:mm A")}
                                placement="right"
                              >
                                <Typography
                                  variant="label-regular"
                                  className={styles.date}
                                  style={{ width: "fit-content" }}
                                >
                                  {moment(program?.created_at).fromNow()}
                                </Typography>
                              </Tooltip>
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>

                    {isEditMode ? (
                      <Grid item display={"flex"} style={{ justifyContent: "flex-end" }}>
                        <Box display={"flex"}>
                          <Box mr={1}>
                            <Button
                              size="small"
                              variant={"secondary"}
                              onClick={() => (isNewProgram ? handleCancel?.() : handleCancelEdit())}
                            >
                              {t("cancelBtn")}
                            </Button>
                          </Box>

                          <Box>
                            <Button
                              variant="primary"
                              disabled={
                                formik.isSubmitting ||
                                !formik.isValid ||
                                formik.values.items.some((item) => item.weight === undefined) ||
                                formik.values.items.length === 0
                              }
                              size="small"
                              onClick={formik.submitForm}
                              isLoading={formik.isSubmitting}
                            >
                              {t("saveBtn")}
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    ) : (
                      canShowDetails && (
                        <Grid item display={"flex"} style={{ justifyContent: "flex-end" }}>
                          <Box display={"flex"}>
                            <Box mr={1} alignSelf="flex-start">
                              <Button
                                size="small"
                                variant="secondary"
                                onClick={() => {
                                  duplicateProgram?.(program as Program)
                                }}
                                disabled={disableDuplicateButton}
                              >
                                {t("duplicateBtn")}
                              </Button>
                            </Box>

                            <Box mr={1} alignSelf="flex-start">
                              <Button
                                size="small"
                                variant="secondary"
                                onClick={() => {
                                  setSelectedVersion(program?.version)
                                  setIsVersioningDialogOpen(true)
                                }}
                              >
                                {t("historyBtn")}
                              </Button>
                            </Box>

                            <Box alignSelf="flex-start">
                              <Button size="small" variant="secondary" onClick={() => setIsEditMode(!isEditMode)}>
                                {t("editBtn")}
                              </Button>
                            </Box>
                          </Box>
                        </Grid>
                      )
                    )}
                  </Grid>
                </Grid>
                {canShowDetails && (
                  <Grid item container width="100%">
                    <Typography variant="p" display="block" style={{ textTransform: "inherit" }}>
                      {t("cardDescription")}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            }
          />

          <CardContent className={styles.cardContent}>
            <Grid container spacing={canShowDetails ? 1 : 0}>
              {canShowDetails ? (
                <FormikProvider value={formik}>
                  <FieldArray
                    name={"items"}
                    render={(arrayHelpers) =>
                      arrayHelpers.form.values.items.map((item: ItemType, index: number) => {
                        return (
                          <Grid
                            container
                            item
                            xs={12}
                            spacing={2}
                            justifyContent={"space-between"}
                            key={
                              (item.item_type === "project" ? item.project?.uuid : item.scorecard_set?.uuid) ?? index
                            }
                          >
                            <Grid item xs={8} md={9.5}>
                              {item.item_type === "project" ? (
                                <LiveModelCard
                                  project={item.project}
                                  isProjectsLoading={isProjectsLoading}
                                  currentIndex={index}
                                  isEdit={isEditMode}
                                  selectedItems={formik.values.items}
                                  handleRemove={() => {
                                    arrayHelpers.remove(index)
                                  }}
                                  onValueChange={(item: { name: string; uuid: string }) =>
                                    formik.setFieldValue(`items[${index}].project`, item)
                                  }
                                />
                              ) : (
                                <ScoreCardSetBlock
                                  scorecardset={item.scorecard_set}
                                  isEdit={isEditMode}
                                  handleRemove={() => {
                                    arrayHelpers.remove(index)
                                  }}
                                  onValueChange={(item: { name: string; uuid: string }) =>
                                    formik.setFieldValue(`items[${index}].scorecard_set`, item)
                                  }
                                  isScorecardSetsLoading={isScorecardSetsLoading}
                                  currentIndex={index}
                                  selectedItems={formik.values.items}
                                />
                              )}
                            </Grid>
                            <Grid item xs={4} md={2.3} alignSelf={"center"}>
                              {isEditMode ? (
                                <InputText
                                  key={item.scorecard_set?.uuid}
                                  id={`items[${index}].weight`}
                                  placeholder={t("inputWeightPlaceholder")}
                                  value={item.weight ? `${parseFloat(item.weight)}` : undefined}
                                  handleChange={formik.handleChange}
                                  handleBlur={formik.handleBlur}
                                  hideDescription
                                  disabled={formik.isSubmitting}
                                  type="number"
                                  fullWidth
                                />
                              ) : (
                                <DataBlock value={`${parseFloat((item.weight ?? 0)?.toString())}%`} />
                              )}
                            </Grid>
                          </Grid>
                        )
                      })
                    }
                  />
                </FormikProvider>
              ) : (
                <Grid
                  container
                  padding={2}
                  border={1}
                  borderColor={"var(--base-border-1)"}
                  bgcolor={"var(--base-background-1)"}
                  borderRadius={1}
                >
                  <Typography variant="label-regular" color="base">
                    {t("hiddenDuePermission")}
                  </Typography>
                </Grid>
              )}

              {/* Add new block section */}
              {isEditMode && (
                <Grid item mt={1}>
                  <Button size="regular" onClick={(event) => setAnchor(event?.currentTarget as HTMLButtonElement)}>
                    + {t("addBlockBtn")}
                  </Button>
                  <Menu
                    key="basic-menu"
                    anchorEl={anchor}
                    open={Boolean(anchor)}
                    onClose={() => setAnchor(null)}
                    menuMaxContent
                  >
                    <Tooltip
                      title={!isProjectsLoading && nonSelectedProjectsExist && t("noliveModelsTooltip")}
                      placement="top"
                    >
                      <MenuItem
                        onClick={() => {
                          formik.setFieldValue("items", [
                            ...formik.values.items,
                            {
                              item_type: "project",
                              project: { uuid: `default-${uuidv4()}`, name: "" },
                              weight: undefined,
                            },
                          ])
                          setAnchor(null)
                        }}
                        disabled={!isProjectsLoading && nonSelectedProjectsExist}
                      >
                        <LiveModelCard menuView project={{ uuid: "default", name: "" }} />
                      </MenuItem>
                    </Tooltip>
                    <Tooltip
                      title={!isScorecardSetsLoading && nonSelectedScoreCardSetsExist && t("noliveScorecardsTooltip")}
                      placement="top"
                    >
                      <MenuItem
                        onClick={() => {
                          formik.setFieldValue("items", [
                            ...formik.values.items,
                            {
                              item_type: "scorecardset",
                              scorecard_set: { uuid: `default-${uuidv4()}`, name: "" },
                              weight: undefined,
                            },
                          ])
                          setAnchor(null)
                        }}
                        disabled={!isScorecardSetsLoading && nonSelectedScoreCardSetsExist}
                      >
                        <ScoreCardSetBlock menuView scorecardset={{ uuid: "default", name: "" }} />
                      </MenuItem>
                    </Tooltip>
                  </Menu>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Fragment>
  )
}
